import { useAuth } from "@hooks/useAuth";
import { useDataMutation } from "@hooks/useData/useDataMutation";
import useGzLog from "@hooks/useGzLog";
import { useInfiniteQuery } from "@tanstack/react-query";
import { getAspectRatio } from "@utils/index";
import { defaultPageSize } from "@utils/queries";
import { getPagination, selectData, softDelete, supabase } from "@utils/serverClient";
import { InsertTemplate, Template, UpdateTemplate } from "types";

export const useTemplates = () => {
  const { log } = useGzLog();
  const { organization } = useAuth();
  const orgId = organization?.id;

  const queryKey = ["templates", orgId]

  const {
    status: templatesFetchStatus,
    data: templates,
    refetch: getTemplates,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey,
    initialPageParam: 0,
    refetchOnMount: false,
    enabled: orgId != undefined,
    queryFn: async ({ pageParam = 0 }) => {
      if (!orgId) return;
      const { from, to } = getPagination(pageParam, defaultPageSize);
      const { data, error } = await selectData({
        table: "template",
        columns: `*`,
      })
        .eq("org_id", orgId)
        .range(from, to)
        .order("created_at", { ascending: false });
      if (error || !data) return;
      return data;
    },
    getNextPageParam: (_lastGroup, groups) =>
      (_lastGroup ?? [])?.length < defaultPageSize ? undefined : groups.length,
  });

  const { mutate: createTemplate, isPending: isCreatingTemplate } = useDataMutation({
    mutationFn: async (template: InsertTemplate) => {
      const { data, error } = await supabase
        .from("template")
        .insert(template)
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to create template",
    optimisticUpdateFn: (oldData: Template[] | undefined, template) => {
      const optimisticTemplate: Template = {
        id: "temp-id-" + Date.now(), // Temporary ID
        deleted: false,
        updated_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
        ...template,
        name: template.name ?? null,
        aspect_ratio: getAspectRatio(template.width, template.height),
        builder_data: null,
        html: null,
        css: null,
        ...template,
        image_src: template.image_src ?? null,
        org_id: template.org_id ?? orgId ?? null,
        status: template.status ?? "published",
        z_indexes: template.z_indexes ?? null,
      };
      return [optimisticTemplate, ...(oldData || [])];
    },
    options: {
      onSuccess: (_, data) => {
        log({
          eventtype: "TemplateCreated",
          name: "TemplateCreated",
          payload: {
            templateId: data.id ?? "undefined",
          },
        });
      },
      onError: (error, template) => {
        log({
          eventtype: "TemplateCreateFailed",
          name: "TemplateCreateFailed",
          payload: {
            templateName: template.name ?? "undefined",
            errorMessage: error.message,
          },
        });
      },
    },
  });

  const { mutate: makePublic, isPending: isMakingPublic } = useDataMutation({
    mutationFn: async (templateId: string) => {
      const { error } = await supabase
        .from("template")
        .update({
          org_id: null,
        })
        .eq("id", templateId);

      if (error) throw error;
      return null;
    },
    queryKeyToInvalidate: queryKey,
    successMessage: "Template made public",
    errorMessage: "Failed to make template public",
    optimisticUpdateFn: (oldData: Template[] | undefined, templateId) => {
      if (!oldData) return oldData;
      return oldData.map((template) =>
        template.id === templateId ? { ...template, org_id: null } : template
      );
    },
  });

  const { mutate: updateTemplate, isPending: isUpdatingTemplate } = useDataMutation({
    mutationFn: async (template: UpdateTemplate) => {
      const { data, error } = await supabase
        .from("template")
        .update(template)
        .match({ id: template.id })
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to update template",
    optimisticUpdateFn: (oldData: Template[] | undefined, template) => {
      if (!oldData) return oldData;
      return oldData.map((t) =>
        t.id === template.id ? { ...t, ...template } : t
      );
    },
    options: {
      onSuccess: (_, data) => {
        log({
          eventtype: "TemplateUpdated",
          name: "TemplateUpdated",
          payload: {
            templateId: data.id ?? "undefined",
            templateName: data.name ?? "undefined",
          },
        });
      },
      onError: (error, template) => {
        log({
          eventtype: "TemplateUpdateFailed",
          name: "TemplateUpdateFailed",
          payload: {
            templateId: template.id ?? "undefined",
            templateName: template.name ?? "undefined",
            errorMessage: error.message,
          },
        });
      },
    },
  });

  const { mutate: deleteTemplate, isPending: isDeletingTemplate } = useDataMutation({
    mutationFn: async (templateId: string) => {
      const { error } = await softDelete({
        table: "template",
      }).match({ id: templateId });
      if (error) throw error;
      return null;
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to delete template",
    optimisticUpdateFn: (oldData: Template[] | undefined, templateId) => {
      if (!oldData) return oldData;
      return oldData.filter((template) => template.id !== templateId);
    },
    options: {
      onSuccess: (_, templateId) => {
        log({
          eventtype: "TemplateDeleted",
          name: "TemplateDeleted",
          payload: {
            templateId,
          },
        });
      },
      onError: (error, templateId) => {
        console.log(error)
        log({
          eventtype: "TemplateDeleteFailed",
          name: "TemplateDeleteFailed",
          payload: {
            templateId: templateId,
            errorMessage: error.message,
          },
        });
      },
    },
  });

  return {
    templates: templates?.pages.flat()?.filter(Boolean).map(t => t!) ?? [],
    createTemplate,
    isCreatingTemplate,
    updateTemplate,
    isUpdatingTemplate,
    deleteTemplate,
    makePublic,
    isMakingPublic,
    isDeletingTemplate,
    templatesFetchStatus,
    getTemplates,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  }
};
